const formats = {
  word: {
    prepare: 'Prepare Word', action: 'Download Word', icon: 'download', busy: 'Preparing your Word file…',
    ready: 'Ready. This Word file contains the prepared document.',
    type: 'wordprocessingml', wrongType: 'The Word response was not a document. Please retry.',
  },
  pdf: {
    prepare: 'Prepare PDF', action: 'Open PDF preview', icon: 'arrow-up-right-from-square', busy: 'Preparing your PDF print preview…',
    ready: 'Ready. Open the preview, then choose Print / Save as PDF.',
    type: 'text/html', wrongType: 'The PDF preview could not be opened. Please retry.',
  },
  markdown: {
    prepare: 'Prepare Markdown', action: 'Download Markdown', icon: 'download', busy: 'Preparing your Markdown file…',
    ready: 'Ready. This Markdown file contains every section of your document.',
    type: 'text/markdown', wrongType: 'The Markdown response was not a document. Please retry.',
  },
}

function filenameFrom(disposition, fallback) {
  const header = disposition || ''
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8) { try { return decodeURIComponent(utf8[1].trim()) } catch (error) { /* use the plain name */ } }
  const plain = header.match(/filename="?([^";]+)"?/i)
  return plain ? plain[1].trim() : fallback
}

export function buildDocumentExport(ui, { session, sync = () => {}, wordUrl, pdfUrl, markdownUrl, title }) {
  const find = selector => ui.dialog.querySelector(selector)
  const prepareButton = find('[data-export-prepare]')
  const downloadButton = find('[data-export-download]')
  const message = find('[data-export-message]')
  const urls = { word: wordUrl, pdf: pdfUrl, markdown: markdownUrl }
  let format = 'word', prepared = null, preparing = false, sequence = 0, request = null
  const discard = () => {
    if (prepared) URL.revokeObjectURL(prepared.url)
    prepared = null
    downloadButton.hidden = true
    prepareButton.hidden = false
  }
  const select = value => {
    sequence += 1
    request?.abort()
    preparing = false
    discard()
    format = formats[value] ? value : 'word'
    const view = formats[format]
    find(`input[value="${format}"]`).checked = true
    prepareButton.disabled = false
    prepareButton.querySelector('[data-delivery-label]').textContent = view.prepare
    downloadButton.querySelector('[data-delivery-label]').textContent = view.action
    find('[data-delivery-action-icon]').innerHTML = `<span class="far fa-${view.icon}"></span>`
    message.textContent = session ? 'Your latest changes will be saved before preparation.' : 'The file will include the saved version of this document.'
  }
  async function prepare() {
    if (preparing) return
    discard()
    const run = ++sequence
    preparing = true
    prepareButton.disabled = true
    message.textContent = session ? 'Saving your document…' : 'Preparing your document…'
    try {
      const view = formats[format]
      sync()
      if (session && !await session.saveAll()) throw Error('Some changes still need attention. Open save status, retry, then prepare again.')
      sync()
      if (session && session.state().status !== 'saved') throw Error('The document changed while saving. Prepare again when you are ready.')
      const revision = session?.revision
      if (run !== sequence) return
      message.textContent = formats[format].busy
      request = new AbortController()
      const timer = setTimeout(() => request?.abort(), 30000)
      let response, blob, warnings = 0, warningSummary = '', disposition = ''
      try {
        response = await fetch(urls[format], { credentials: 'same-origin', signal: request.signal, cache: 'no-store' })
        if (!response.ok || response.redirected) {
          const reason = format === 'markdown' ? response.headers.get('X-Markdown-Error') : null
          throw Error(reason || 'Export could not be prepared. Your writing is saved; please retry.')
        }
        const type = response.headers.get('Content-Type') || ''
        if (!type.includes(view.type)) throw Error(view.wrongType)
        warnings = Number(response.headers.get('X-Markdown-Warnings') || 0)
        warningSummary = response.headers.get('X-Markdown-Warning-Summary') || ''
        disposition = response.headers.get('Content-Disposition') || ''
        blob = await response.blob()
      } finally { clearTimeout(timer) }
      sync()
      if (run !== sequence) return
      if (session && (revision !== session.revision || session.state().status !== 'saved')) throw Error('The document changed during preparation. Prepare again to include your latest writing.')
      const fallback = `${(title() || 'document').replace(/[\\/:*?"<>|]/g, '-').slice(0,150)}.${format === 'markdown' ? 'md' : 'docx'}`
      prepared = {
        url: URL.createObjectURL(blob), revision,
        filename: format === 'pdf' ? fallback : filenameFrom(disposition, fallback),
      }
      prepareButton.hidden = true
      downloadButton.hidden = false
      const notes = format === 'markdown' && warnings > 0
        ? ` ${warnings} note${warnings === 1 ? '' : 's'}${warningSummary ? `: ${warningSummary}` : ' — open save status for details.'}`
        : ''
      message.textContent = `${formats[format].ready}${notes}`
    } catch (error) {
      if (run === sequence) message.textContent = error.name === 'AbortError' ? 'Preparation timed out. Please retry.' : error.message
    } finally {
      if (run === sequence) { preparing = false; prepareButton.disabled = false }
    }
  }
  const changed = () => {
    if (prepared && session && (session.revision !== prepared.revision || session.state().status !== 'saved')) {
      discard()
      message.textContent = 'The document changed. Prepare again to include your latest writing.'
    }
  }
  const download = () => {
    sync();changed()
    if (!prepared) return
    if (format === 'pdf') {
      const preview = window.open(prepared.url, '_blank')
      if (!preview) { message.textContent = 'Your browser blocked the preview. Allow popups and choose Open PDF preview again.'; return }
      preview.opener = null
      message.textContent = 'Preview opened. Use Print / Save as PDF in that tab.'
    } else {
      const link = document.createElement('a');link.href=prepared.url;link.download=prepared.filename
      document.body.append(link);link.click();link.remove()
      message.textContent = `Download requested. If it did not start, choose ${formats[format].action} again.`
    }
  }
  prepareButton.addEventListener('click', prepare)
  downloadButton.addEventListener('click', download)
  ui.dialog.querySelectorAll('input[name="document-export-format"]').forEach(input => input.addEventListener('change', () => select(input.value)))
  find('[data-export-saves]').addEventListener('click', () => ui.open('save'))
  return {
    open(value, opener) { select(value);ui.open('export', opener) }, changed,
    destroy() { sequence += 1;request?.abort();discard() },
  }
}
